import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LeadDetailsComponent from "../../../global/components/sidePanel/leadDetail";
import {
  assignToMembers,
  changeAt,
  changeStage,
  deleteReminder,
  getAllSource,
  getAllTeamData,
  getAllTeamMembersData,
  getReminder,
  saveReminder,
} from "./ShowAll/ShowAllService";
import {
  getRemark,
  saveRemark,
} from "../../MyLead/components/ShowAll/ShowAllService";
import { getStage } from "../../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { updatePersonalDetail } from "./stepper/StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";
import { YhButton, YhModal } from "../../../ui";
import { loadViewerTeamIds } from "../../../helpers/assignmentScope";

type Props = {
  lead: any;
  open: boolean;
  onClose: () => void;
  onLeadChange: (next: any) => void;
  onOpenTab?: (tab: "reminder" | "notes") => void;
  /** Sync current Assigned To / Member selection to parent (e.g. Add Task modal) */
  onAssignSelectionChange?: (selection: {
    teams: Array<{ id: string; label: string }>;
    members: Array<{ id: string; label: string }>;
    teamOptions: Array<{ id: string; label: string }>;
    memberOptions: Array<{ id: string; label: string }>;
  }) => void;
};

const AllLeadSideDrawer: React.FC<Props> = ({
  lead,
  open,
  onClose,
  onLeadChange,
  onOpenTab,
  onAssignSelectionChange,
}) => {
  const [allSatgedata, setAllStagedata] = useState<any[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [customerConfirm, setCustomerConfirm] = useState<{
    open: boolean;
    stage: string;
    lead: any;
  }>({ open: false, stage: "", lead: null });
  const [convertingCustomer, setConvertingCustomer] = useState(false);
  const [leadOption, setLeadOption] = useState<any[]>([]);
  const [leadOptionMembers, setLeadOptionMembers] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [selectedOptionsMembers, setSelectedOptionsMembers] = useState<any[]>(
    []
  );
  const [reminderStatus, setReminderStatus] = useState("No Reminder");
  const [reminderData, setReminderData] = useState<any>({
    title: "",
    date: "",
    time: "",
    notes: "",
    notes_remark: "",
  });
  const [responseValuereminder, setResponseValuereminder] = useState<any>({
    value: [],
  });
  const [responseValueremark, setResponseValueremark] = useState<any>({
    value: [],
  });
  const [addinationalValue, setAddinationalValue] = useState<any>([]);

  const cleanId = (val: any) =>
    String(val ?? "")
      .replace(/['"]/g, "")
      .trim();

  const parseAssignedTeamIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => {
          if (item == null) return "";
          if (typeof item === "string" || typeof item === "number") {
            return cleanId(item);
          }
          return cleanId(item.team_id || item._id || item.id);
        })
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => cleanId(s))
        .filter((s) => s && s !== "NA");
    }
    return [];
  };

  /** Never use team_id on user objects — it breaks member preselection. */
  const parseAssignedMemberIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => {
          if (item == null) return "";
          if (typeof item === "string" || typeof item === "number") {
            return cleanId(item);
          }
          return cleanId(item.user_id || item.userId || item._id || item.id);
        })
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => cleanId(s))
        .filter((s) => s && s !== "NA");
    }
    return [];
  };

  const getAssignedTeamIds = (assignTo: any) =>
    new Set(parseAssignedTeamIds(assignTo));

  const getAssignedMemberIds = (assignToUser: any) =>
    new Set(parseAssignedMemberIds(assignToUser));

  const filterMembersForAssignedTeams = (
    assignToUser: any,
    assignedTeamIds: Set<string>
  ) => {
    if (!Array.isArray(assignToUser)) return assignToUser;
    if (!assignedTeamIds.size) return assignToUser;
    const matched = assignToUser.filter((m: any) => {
      try {
        const raw = m?.team_id;
        if (raw == null || raw === "" || raw === "NA") return true;
        const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(arr) || arr.length === 0) return true;
        return arr.some((tid: any) => assignedTeamIds.has(String(tid)));
      } catch {
        return true;
      }
    });
    return matched.length ? matched : assignToUser;
  };

  const isFlagOn = (val: any) =>
    val === 1 || val === "1" || val === true;

  const emitAssignSelection = (
    teams: any[],
    members: any[],
    teamOptions: any[],
    memberOptions: any[]
  ) => {
    if (!onAssignSelectionChange) return;
    onAssignSelectionChange({
      teams: (teams || []).map((t: any) => ({
        id: String(t.id),
        label: t.label || String(t.id),
      })),
      members: (members || []).map((m: any) => ({
        id: String(m.id),
        label: m.label || String(m.id),
      })),
      teamOptions: (teamOptions || []).map((t: any) => ({
        id: String(t.id),
        label: t.label || String(t.id),
      })),
      memberOptions: (memberOptions || []).map((m: any) => ({
        id: String(m.id),
        label: m.label || String(m.id),
      })),
    });
  };

  const loadTeams = async (leadData: any) => {
    const leadId = leadData?._id;
    if (!leadId) return;
    try {
      const assignedTeamIds = getAssignedTeamIds(leadData.AssignTo);
      const leadMembersForSelect = filterMembersForAssignedTeams(
        leadData.AssignToUser,
        assignedTeamIds
      );
      const assignedMemberIds = getAssignedMemberIds(leadMembersForSelect);

      const response1 = await getAllTeamData(leadId);
      const assignToData = (response1?.data || []).flatMap((team: any) => {
        const subTeams =
          team.sub_teams?.map((subTeam: any) => {
            const tid = String(subTeam._id);
            const isAssigned =
              isFlagOn(subTeam?.is_available) || assignedTeamIds.has(tid);
            return {
              id: subTeam._id,
              label: subTeam.team_name,
              selected: isAssigned ? 1 : 0,
              isParent: false,
              teamMembers: subTeam.team_members?.length
                ? subTeam.team_members.map((member: any) => ({
                    // Prefer user account _id (same as JWT) so My Leads mapping matches
                    id: cleanId(member.users?.[0]?._id || member.userId),
                    label: member.users?.[0]?.name,
                    selected: 0,
                  }))
                : null,
            };
          }) || [];

        const tid = String(team._id);
        const mainAssigned =
          isFlagOn(team?.is_available) || assignedTeamIds.has(tid);
        const mainTeam = {
          id: team._id,
          label: team.team_name,
          selected: mainAssigned ? 1 : 0,
          isParent: false,
          teamMembers: team.team_members?.length
            ? team.team_members.map((member: any) => ({
                id: cleanId(member.users?.[0]?._id || member.userId),
                label: member.users?.[0]?.name,
                selected: 0,
              }))
            : null,
        };
        return [mainTeam, ...subTeams];
      });

      if (Array.isArray(leadData.AssignTo)) {
        leadData.AssignTo.forEach((t: any) => {
          const tid = String(t?.team_id || t?._id || t?.id || "").replace(
            /['"]/g,
            ""
          );
          const label = t?.team_name || t?.label || tid;
          const idx = assignToData.findIndex((e: any) => String(e.id) === tid);
          if (idx >= 0) assignToData[idx].selected = 1;
          else if (tid) {
            assignToData.push({
              id: tid,
              label,
              selected: 1,
              isParent: false,
              teamMembers: null,
            });
          }
        });
      } else {
        assignedTeamIds.forEach((tid) => {
          const idx = assignToData.findIndex((e: any) => String(e.id) === tid);
          if (idx >= 0) assignToData[idx].selected = 1;
          else if (tid) {
            assignToData.push({
              id: tid,
              label: tid,
              selected: 1,
              isParent: false,
              teamMembers: null,
            });
          }
        });
      }

      const viewerTeamIds = await loadViewerTeamIds();
      const visibleTeams =
        viewerTeamIds == null
          ? assignToData
          : assignToData.filter((team: any) => {
              const id = String(team?.id || "");
              const alreadySelected = Number(team?.selected) === 1;
              return alreadySelected || viewerTeamIds.includes(id);
            });

      setLeadOption(visibleTeams);
      const selectedData = assignToData
        .filter((e: any) => e?.selected == 1)
        .map((e: any) => ({
          id: e.id,
          label: e.label,
          selected: 1,
          isParent: false,
          teamMembers: Array.isArray(e.teamMembers)
            ? e.teamMembers.map((member: any) => ({
                id: member.id,
                label: member.label,
                selected: 0,
              }))
            : null,
        }));
      setSelectedOptions(selectedData);

      const seededMembers = Array.isArray(leadMembersForSelect)
        ? leadMembersForSelect.map((m: any) => ({
            pId: "",
            id: cleanId(m?.user_id || m?.userId || m?._id || m?.id),
            label: m?.name || m?.fullName || m?.userName || "Member",
            selected: 1,
          })).filter((m: any) => m.id)
        : parseAssignedMemberIds(leadMembersForSelect).map((uid) => ({
            pId: "",
            id: uid,
            label: uid,
            selected: 1,
          }));

      if (seededMembers.length) {
        setLeadOptionMembers(seededMembers);
        setSelectedOptionsMembers(seededMembers);
      }

      const teamIds = selectedData.map((e: any) => e.id);
      if (!teamIds.length) {
        if (!seededMembers.length) {
          setLeadOptionMembers([]);
          setSelectedOptionsMembers([]);
        }
        emitAssignSelection(
          selectedData,
          seededMembers,
          assignToData,
          seededMembers
        );
        return;
      }

      try {
        const membersRes = await getAllTeamMembersData({
          team_id: teamIds,
          lead_id: leadId,
        });
        const data =
          (membersRes?.data || []).flatMap((member: any) =>
            (member?.team_members || [])
              .map((mem: any) => {
                if (!mem.users?.[0]) return null;
                const uid = cleanId(mem.users[0]._id || mem.userId);
                const officeUid = cleanId(mem.userId);
                const isAssigned =
                  assignedMemberIds.has(uid) ||
                  (officeUid ? assignedMemberIds.has(officeUid) : false);
                return {
                  pId: member._id,
                  id: cleanId(mem.users[0]._id) || officeUid,
                  label: mem.users[0]?.name,
                  selected: isAssigned ? 1 : 0,
                };
              })
              .filter(Boolean)
          ) || [];
        seededMembers.forEach((m: any) => {
          const uid = String(m.id);
          const idx = data.findIndex((x: any) => String(x.id) === uid);
          if (idx >= 0) data[idx].selected = 1;
          else data.push({ ...m, selected: 1 });
        });

        const visibleMembers =
          viewerTeamIds == null
            ? data
            : data.filter((member: any) => {
                const teamId = String(member?.pId || "");
                const alreadySelected = Number(member?.selected) === 1;
                return (
                  alreadySelected ||
                  !teamId ||
                  viewerTeamIds.includes(teamId)
                );
              });

        setLeadOptionMembers(visibleMembers);
        const selected = visibleMembers.filter((m: any) => m.selected === 1);
        const finalMembers = selected.length ? selected : seededMembers;
        setSelectedOptionsMembers(finalMembers);
        emitAssignSelection(
          selectedData,
          finalMembers,
          visibleTeams,
          visibleMembers
        );
      } catch {
        if (seededMembers.length) {
          setLeadOptionMembers(seededMembers);
          setSelectedOptionsMembers(seededMembers);
        }
        emitAssignSelection(
          selectedData,
          seededMembers,
          assignToData,
          seededMembers
        );
      }
    } catch {
      setLeadOption([]);
      setSelectedOptions([]);
      setLeadOptionMembers([]);
      setSelectedOptionsMembers([]);
      emitAssignSelection([], [], [], []);
    }
  };

  const loadNotesAndReminders = async (leadId: string) => {
    try {
      const [rem, remark] = await Promise.all([
        getReminder(leadId).catch(() => null),
        getRemark(leadId).catch(() => null),
      ]);
      setResponseValuereminder({
        value: Array.isArray(rem?.data) ? rem.data : [],
      });
      setResponseValueremark({
        value: Array.isArray(remark?.data) ? remark.data : [],
      });
    } catch {
      setResponseValuereminder({ value: [] });
      setResponseValueremark({ value: [] });
    }
  };

  useEffect(() => {
    if (!open || !lead?._id) return;
    setAddinationalValue(lead.dynamicFields || []);
    getStage()
      .then((res) => setAllStagedata(res?.data || []))
      .catch(() => setAllStagedata([]));
    getAllSource()
      .then((res) =>
        setSourceOptions(
          (res?.data || []).map((e: any) => e.source_name).filter(Boolean)
        )
      )
      .catch(() => setSourceOptions([]));
    loadTeams(lead);
    loadNotesAndReminders(lead._id);
  }, [open, lead?._id, lead?.AssignTo, lead?.AssignToUser]);

  const applyStageChange = async (
    data: any,
    value: string,
    reason?: string
  ) => {
    try {
      const body: any = { id: data?._id, stage: value };
      if (reason) {
        body.reason = reason;
        body.stageReason = reason;
      }
      const response = await changeStage(body);
      if (response?.success === 1) {
        onLeadChange({ ...lead, stage: value });
        const converted =
          response?.customerConverted ||
          String(value).trim().toLowerCase() === "customer";
        openSuccessNotification(
          converted
            ? response?.message || "Lead converted to customer successfully"
            : response?.message || "Stage updated"
        );
        if (converted) {
          onClose();
        }
        return true;
      }
      openErrorNotification(response?.message || "Failed to update stage");
      return false;
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to update stage");
      return false;
    }
  };

  const handleAutocompleteChange = async (
    name: string,
    value: any,
    data: any,
    opts?: { reasonAlreadySaved?: boolean; reason?: string }
  ) => {
    if (name === "stage") {
      if (String(value || "").trim().toLowerCase() === "customer") {
        setCustomerConfirm({ open: true, stage: value, lead: data });
        return;
      }
      await applyStageChange(data, value, opts?.reason);
      return;
    }

    if (name === "source") {
      try {
        const body = {
          id: data?._id || data?.id,
          formId: data.formId,
          form_name: data.form_name,
          developerId: data.developerId,
          projectId: data.projectId,
          projecttypeId: data.projecttypeId,
          leadName: data.leadName,
          leadEmail: data.leadEmail,
          leadPhone: data.leadPhone,
          dynamicFields: data.dynamicFields || [],
          AssignTo: data.AssignTo,
          AssignToUser: data.AssignToUser,
          stage: data.stage || "NA",
          status: data.status,
          source: value,
        };
        const response = await updatePersonalDetail(body);
        if (response?.success) {
          onLeadChange({ ...lead, source: value });
          openSuccessNotification(
            response?.message || "Source updated successfully"
          );
        } else {
          openErrorNotification(response?.message || "Failed to update source");
        }
      } catch (e: any) {
        openErrorNotification(e?.message || "Failed to update source");
      }
    }
  };

  const confirmConvertCustomer = async () => {
    if (!customerConfirm.lead?._id || !customerConfirm.stage) return;
    try {
      setConvertingCustomer(true);
      await applyStageChange(customerConfirm.lead, customerConfirm.stage);
      setCustomerConfirm({ open: false, stage: "", lead: null });
    } finally {
      setConvertingCustomer(false);
    }
  };

  const handleTeamMultiChange = (values: Array<string | number>) => {
    const nextIds = new Set(values.map(String));
    const nextSelected = leadOption
      .filter((o: any) => !o.isParent && nextIds.has(String(o.id)))
      .map((o: any) => ({ ...o, selected: 1 }));

    setSelectedOptions(nextSelected);
    const nextLeadOption = leadOption.map((item: any) => ({
      ...item,
      selected: nextIds.has(String(item.id)) ? 1 : 0,
    }));
    setLeadOption(nextLeadOption);
    const nextMemberOptions = nextSelected.flatMap((opt: any) =>
      (opt.teamMembers || []).map((m: any) => ({
        pId: opt.id,
        id: m.id,
        label: m.label,
        selected: 0,
      }))
    );
    setLeadOptionMembers(nextMemberOptions);
    setSelectedOptionsMembers((prev: any) => {
      const next = (prev || []).filter((m: any) =>
        nextIds.has(String(m.pId))
      );
      emitAssignSelection(
        nextSelected,
        next,
        nextLeadOption,
        nextMemberOptions
      );
      return next;
    });
  };

  const handleMemberMultiChange = (values: Array<string | number>) => {
    const nextIds = new Set(values.map(String));
    const uniqueMembers = Array.from(
      new Map(
        (leadOptionMembers || []).map((member: any) => [member.id, member])
      ).values()
    );
    const nextSelected = uniqueMembers
      .filter((o: any) => nextIds.has(String(o.id)))
      .map((o: any) => ({ ...o, selected: 1 }));

    setSelectedOptionsMembers(nextSelected);
    const nextMemberOptions = (leadOptionMembers || []).map((item: any) => ({
      ...item,
      selected: nextIds.has(String(item.id)) ? 1 : 0,
    }));
    setLeadOptionMembers(nextMemberOptions);
    emitAssignSelection(
      selectedOptions,
      nextSelected,
      leadOption,
      nextMemberOptions
    );
  };

  const handelAssignToUser = async (dynamicGridValue: any) => {
    const leadId = dynamicGridValue?._id || dynamicGridValue?.id;
    if (!leadId) {
      openErrorNotification("Lead not found");
      return;
    }

    const teamIds = (selectedOptions || [])
      .map((item: any) => cleanId(item.id))
      .filter(Boolean);
    const memberIds = (selectedOptionsMembers || [])
      .map((item: any) => cleanId(item.id))
      .filter(Boolean);

    if (!teamIds.length && !memberIds.length) {
      openErrorNotification("Select a team or member to assign");
      return;
    }

    try {
      let nextAssignTo = dynamicGridValue?.AssignTo;
      let nextAssignToUser = dynamicGridValue?.AssignToUser;

      // Persist teams first (Assigned To was clearing because members-only path skipped this)
      if (teamIds.length) {
        const idTeamVal = teamIds.map((id) => `'${id}'`).join(",");
        const teamRes = await changeAt({
          id: leadId,
          AssignTo: idTeamVal,
        });
        if (!(teamRes?.success === 1 || teamRes?.success === true)) {
          openErrorNotification(teamRes?.message || "Failed to assign team");
          return;
        }
        nextAssignTo =
          teamRes?.data?.AssignTo != null ? teamRes.data.AssignTo : idTeamVal;
      }

      if (memberIds.length) {
        const memberRes = await assignToMembers({
          id: leadId,
          AssignToUser: memberIds.join(","),
          AssignTo: teamIds.length
            ? teamIds.map((id) => `'${id}'`).join(",")
            : undefined,
          stage: dynamicGridValue?.stage || "Initial",
        });
        if (!(memberRes?.success === 1 || memberRes?.success === true)) {
          openErrorNotification(
            memberRes?.message || "Failed to assign members"
          );
          return;
        }
        nextAssignToUser =
          memberRes?.data?.AssignToUser != null
            ? memberRes.data.AssignToUser
            : memberIds.join(",");
        if (memberRes?.data?.AssignTo != null) {
          nextAssignTo = memberRes.data.AssignTo;
        }
        openSuccessNotification("Lead assigned successfully");
      } else {
        openSuccessNotification("Team assigned successfully");
      }

      const updatedLead = {
        ...dynamicGridValue,
        ...lead,
        AssignTo: nextAssignTo,
        AssignToUser: nextAssignToUser,
      };
      onLeadChange(updatedLead);
      await loadTeams(updatedLead);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to assign");
    }
  };

  const handleChangeRadio = (e: any) => {
    setReminderStatus(e.target.value);
  };

  const handleDateTimeChange = (event: any) => {
    const selectedDateTime = event?.target?.value || "";
    if (!selectedDateTime || !selectedDateTime.includes("T")) {
      setReminderData((prev: any) => ({ ...prev, date: "", time: "" }));
      return;
    }
    setReminderData((prev: any) => ({
      ...prev,
      date: selectedDateTime.split("T")[0],
      time: selectedDateTime.split("T")[1],
    }));
  };

  const handelSideSave = async (body: any) => {
    try {
      const response = await saveReminder(body);
      if (response?.success === 1) {
        openSuccessNotification(response?.message || "Reminder saved");
        setReminderData({
          title: "",
          date: "",
          time: "",
          notes: "",
          notes_remark: "",
        });
        setReminderStatus("No Reminder");
        await loadNotesAndReminders(body.leadId);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save reminder");
    }
  };

  const handelSideSaveRemark = async (body: any) => {
    try {
      const payload = {
        leadId: body.leadId,
        notes: body.notes_remark,
        date: new Date().toISOString().split("T")[0],
        time: new Date().toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        title: "",
      };
      const response = await saveRemark(payload);
      if (response?.success === 1) {
        openSuccessNotification(response?.message || "Note saved");
        setReminderData((prev: any) => ({ ...prev, notes_remark: "" }));
        await loadNotesAndReminders(body.leadId);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save note");
    }
  };

  const handelRemoveReminder = async (item: any) => {
    try {
      await deleteReminder(item?._id || item?.id);
      openSuccessNotification("Reminder removed");
      if (lead?._id) await loadNotesAndReminders(lead._id);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to remove reminder");
    }
  };

  const safeLead = {
    ...lead,
    updatedAt: lead?.updatedAt || new Date().toISOString(),
  };

  return (
    <aside
      aria-hidden={!open}
      className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${
        open
          ? "w-[280px] opacity-100 sm:w-[300px] xl:w-[340px]"
          : "pointer-events-none w-0 opacity-0"
      }`}
    >
      <div
        className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "100%" }}
      >
        <button
          type="button"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          onClick={onClose}
          aria-label="Close lead details"
        >
          <CloseIcon sx={{ width: 14, height: 14 }} />
        </button>

        <div className="h-full w-full overflow-auto p-4 pt-12 [scrollbar-width:thin] [scroll-padding-bottom:96px]">
          {open && lead?._id ? (
            <LeadDetailsComponent
              dynamicGridValue={safeLead}
              handleAutocompleteChange={handleAutocompleteChange}
              selectedOptions={selectedOptions}
              leadOption={leadOption}
              leadOptionMembers={leadOptionMembers}
              selectedOptionsMembers={selectedOptionsMembers}
              handleChangeRadio={handleChangeRadio}
              reminderStatus={reminderStatus}
              setReminderStatus={setReminderStatus}
              setReminderData={setReminderData}
              currentDateTime=""
              handleDateTimeChange={handleDateTimeChange}
              handelAssignToUser={handelAssignToUser}
              handelSideSave={handelSideSave}
              reminderData={reminderData}
              responseValuereminder={responseValuereminder}
              addinationalValue={addinationalValue}
              isMyLead={false}
              isTeamLead={false}
              responseValueremark={responseValueremark}
              handelSideSaveRemark={handelSideSaveRemark}
              handelRemoveReminder={handelRemoveReminder}
              onRemindersChange={(rows: any[]) => {
                setResponseValuereminder({ value: rows });
              }}
              openCustomer={() => undefined}
              setLeadData={() => undefined}
              handleTeamMultiChange={handleTeamMultiChange}
              handleMemberMultiChange={handleMemberMultiChange}
              allSatgedata={allSatgedata}
              sourceOptions={sourceOptions}
              customerDetail
              setSelectedTab={(idx: number) => {
                if (idx === 3) onOpenTab?.("reminder");
                if (idx === 4) onOpenTab?.("notes");
              }}
            />
          ) : null}
        </div>
      </div>

      <YhModal
        open={customerConfirm.open}
        onClose={() =>
          !convertingCustomer &&
          setCustomerConfirm({ open: false, stage: "", lead: null })
        }
        title="Convert to Customer"
        width={420}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={convertingCustomer}
              onClick={() =>
                setCustomerConfirm({ open: false, stage: "", lead: null })
              }
            >
              No
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={convertingCustomer}
              onClick={confirmConvertCustomer}
            >
              {convertingCustomer ? "Converting..." : "Yes"}
            </YhButton>
          </div>
        }
      >
        <p className="text-sm text-[#374151]">
          Are you sure to make customer?
        </p>
        {customerConfirm.lead?.leadName ? (
          <p className="mt-2 text-xs text-[#6B7280]">
            Lead:{" "}
            <span className="font-semibold text-[#111827]">
              {customerConfirm.lead.leadName}
            </span>
          </p>
        ) : null}
      </YhModal>
    </aside>
  );
};

export default AllLeadSideDrawer;
